import '../index.scss';

import { LuiIcon } from '@linzjs/lui';
import { IconName } from '@linzjs/lui/dist/components/LuiIcon/LuiIcon';
import clsx from 'clsx';
import { forwardRef, PropsWithChildren, useContext, useEffect } from 'react';

import { tabToNextSiblingElement, tabToPreviousSiblingElement } from '../common/util';
import { RibbonButtonSliderContext } from './RibbonButtonSliderContext';
import { RibbonMenuContext } from './RibbonMenu';

export interface RibbonMenuOption {
  id?: string;
  popoverTarget?: string;
  popoverTargetAction?: 'toggle' | 'show' | 'hide' | undefined;
  anchorName?: string;
  title?: string;
  icon?: IconName;
  disabled?: boolean;
  className?: string;
  testId?: string;
  onClick?: () => void;
}

export const RibbonMenuOption = forwardRef<HTMLButtonElement, PropsWithChildren<RibbonMenuOption>>(
  function RibbonButton(
    { id, popoverTarget, popoverTargetAction, anchorName, title, icon, children, className, disabled, testId, onClick },
    ref,
  ) {
    const { menuUniqueId, autoClose } = useContext(RibbonButtonSliderContext);
    const { hasIcon, setHasIcon } = useContext(RibbonMenuContext);

    useEffect(() => {
      setHasIcon(!!icon);
    }, [icon, setHasIcon]);

    return (
      <button
        ref={ref}
        id={id}
        style={{ anchorName }}
        {...{ ['popovertarget' as never]: popoverTarget ?? menuUniqueId }}
        {...{ ['popovertargetaction' as never]: popoverTargetAction ?? (autoClose ? 'toggle' : 'show') }}
        type="button"
        title={title}
        data-testid={testId}
        disabled={disabled}
        onKeyDown={({ code }) => {
          if (code === 'ArrowDown') {
            tabToNextSiblingElement();
          }
          if (code === 'ArrowUp') {
            tabToPreviousSiblingElement();
          }
        }}
        onMouseMove={({ currentTarget }) => {
          currentTarget?.focus?.();
        }}
        onClick={onClick}
        className={clsx(className, 'RibbonMenuOption', disabled && 'RibbonMenuOption-disabled')}
      >
        <div style={{ display: 'flex' }}>{icon && <LuiIcon name={icon} alt={title ?? ''} size={'md'} />}</div>
        <div style={hasIcon && !icon ? { paddingLeft: 24 } : {}}>{children}</div>
      </button>
    );
  },
);
